import { type FC, type PropsWithChildren } from 'react';
import { type ParsedUrlQuery } from 'querystring';
import { ChevronUp } from 'lucide-react';
import { ulid } from 'ulid';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { type Pagination as PaginationTypes } from '@/types';
import { DOTS, usePagination } from '@/hooks/usePagination';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import Link from 'next/link';

export const CustomPagination = ({
  totalCount,
  siblingCount = 1,
  currentPage,
  pageSize,
  className,
  handleNext,
  handlePrev,
  onPageChange,
  deselectAssetsOnPageChange,
  onlyIcons = false,
}: PaginationTypes) => {
  const { isMobile } = useBreakpoint();
  const { push, query } = useRouter();

  const { deselectAllItems } = useGallerySelection();

  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  });

  return (
    <Pagination className={className}>
      <PaginationContent className={cn(isMobile && 'flex w-full justify-between')}>
        <PaginationItem>
          <PaginationPrevious
            onClick={handlePrev?.onClick}
            href={handlePrev?.href ?? {}}
            disabled={handlePrev?.disabled}
            onlyIcons={onlyIcons}
          />
        </PaginationItem>

        <ContentWrapper isMobile={isMobile} currentPage={currentPage}>
          {paginationRange?.map((pageNumber) => {
            return (
              <ItemWrapper
                push={() => push}
                key={ulid()}
                pageNumber={pageNumber}
                isMobile={isMobile}
                deselectAssetsOnPageChange={deselectAssetsOnPageChange}
                deselectAllItems={deselectAllItems}
                query={query}
                currentPage={currentPage}
                onPageChange={onPageChange}
              >
                {pageNumber}
              </ItemWrapper>
            );
          })}
        </ContentWrapper>

        <PaginationItem>
          <PaginationNext
            onClick={handleNext?.onClick}
            href={handleNext?.href ?? {}}
            disabled={handleNext?.disabled}
            onlyIcons={onlyIcons}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};

const ContentWrapper: FC<PropsWithChildren<{ isMobile: boolean; currentPage?: number }>> = ({
  children,
  isMobile,
  currentPage,
}) => {
  const tPagination = useTranslations('pagination');

  return isMobile ? (
    <DropdownMenu>
      <DropdownMenuTrigger className="group flex items-center dark:text-text-base">
        {tPagination('pageShort', { page: currentPage === 0 || currentPage === undefined ? 1 : currentPage })}
        <ChevronUp className={cn('ml-2 transition-transform duration-150 icon-4 group-data-[state=open]:rotate-180')} />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel className="flex justify-center">{tPagination('pages')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    children
  );
};

const ItemWrapper: FC<
  PropsWithChildren<{
    isMobile: boolean;
    pageNumber: string | number;
    deselectAllItems: () => void;
    deselectAssetsOnPageChange?: boolean;
    query: ParsedUrlQuery;
    currentPage?: number;
    push: (query: {
      query: { keyword?: string | Array<string>; page?: number | string; results?: number | string | Array<string> };
    }) => void;
    onPageChange?: (page: number | undefined) => void;
  }>
> = ({
  isMobile,
  children,
  pageNumber,
  currentPage,
  query,
  onPageChange,
  deselectAllItems,
  deselectAssetsOnPageChange,
}) => {
  if (pageNumber === DOTS) {
    return isMobile ? (
      <DropdownMenuLabel
        className="flex items-center justify-center text-neutral-300"
        onClick={(e) => e.preventDefault()}
      >
        ...
      </DropdownMenuLabel>
    ) : (
      <PaginationItem className="dark:text-text-base">&#8230;</PaginationItem>
    );
  }

  return isMobile ? (
    (currentPage === 0 && pageNumber === 1) || pageNumber === currentPage ? (
      <DropdownMenuItem disabled className="justify-center" onClick={(e) => e.preventDefault()}>
        {children}
      </DropdownMenuItem>
    ) : (
      <Link href={{ query: { ...query, page: pageNumber } }}>
        <DropdownMenuItem className="justify-center">{children}</DropdownMenuItem>
      </Link>
    )
  ) : (
    <PaginationItem key={`page-${pageNumber}`}>
      <PaginationLink
        onClick={() => {
          if (deselectAssetsOnPageChange) {
            deselectAllItems();
          }

          if (onPageChange !== undefined) {
            onPageChange(Number(pageNumber));
          }
        }}
        href={
          onPageChange === undefined
            ? {
                query: {
                  ...query,
                  page: pageNumber,
                },
              }
            : { query }
        }
        isActive={(currentPage === 0 && pageNumber === 1) || pageNumber === currentPage}
      >
        {children}
      </PaginationLink>
    </PaginationItem>
  );
};
